import React, { useState } from 'react';
import { SigeBlock, BlockTyp } from './types';
import { supabase } from '../../services/supabaseClient';
import { Contact } from '../../types';
import { Plus, Settings2, Trash2 } from 'lucide-react';
import { NotrufplanEditor } from './NotrufplanEditor';
import { BlockEditor } from './BlockEditor';
import { anker } from '../../tutorial/anker';

interface BlockManagerProps {
  sigeProjektId?: string;
  bloecke: SigeBlock[];
  beteiligte: Contact[];
  adresse?: string;
  onBloeckeChange: (b: SigeBlock[]) => void;
}

const AVAILABLE_BLOCKS: { typ: BlockTyp, label: string }[] = [
  { typ: 'notrufplan', label: 'Notrufplan' },
  { typ: 'risk_assessment', label: 'Last Minute Risk Assessment' },
  { typ: 'piktogrammfeld', label: 'Gebots-/Warnzeichen' },
  { typ: 'legende', label: 'Legende' },
  { typ: 'ersthelfer', label: 'Ersthelfer Info' },
  { typ: 'grundsatz', label: 'Grundsätze' },
  { typ: 'besonderheiten', label: 'Besonderheiten' },
];

export const BlockManager: React.FC<BlockManagerProps> = ({ sigeProjektId, bloecke, beteiligte, adresse, onBloeckeChange }) => {
  const [editBlock, setEditBlock] = useState<SigeBlock | null>(null);

  const handleAddBlock = async (typ: BlockTyp, label: string) => {
    if (!sigeProjektId) return;
    if (bloecke.some(b => b.typ === typ)) {
      // Block-Typ existiert schon → direkt Editor öffnen
      setEditBlock(bloecke.find(b => b.typ === typ)!);
      return;
    }
    try {
      const { data } = await supabase.from('sige_bloecke').insert({
        sige_projekt_id: sigeProjektId,
        typ,
        titel: label,
        inhalt: {}
      }).select().single();

      if (data) {
        onBloeckeChange([...bloecke, data as SigeBlock]);
        setEditBlock(data as SigeBlock);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await supabase.from('sige_bloecke').delete().eq('id', id);
      onBloeckeChange(bloecke.filter(b => b.id !== id));
    } catch (e) { console.error(e); }
  };

  const handleSaved = (saved: SigeBlock) => {
    onBloeckeChange(bloecke.some(b => b.id === saved.id)
      ? bloecke.map(b => b.id === saved.id ? saved : b)
      : [...bloecke, saved]);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="p-4 border-b border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-800">Plan-Blöcke</h2>
        <p className="text-xs text-slate-500 mt-1">Notrufplan, LMRA, Piktogramme, Legende u.a.</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Aktive Blöcke im Plan</div>
          <div className="space-y-2">
            {bloecke.length === 0 && <div className="text-sm text-slate-400 italic">Keine Blöcke hinzugefügt.</div>}
            {bloecke.map((b, blockIndex) => (
              <div key={b.id} className="bg-white border text-sm border-slate-200 p-3 rounded-lg shadow-sm flex items-start gap-3 group">
                <div className="flex-1">
                  <div className="font-medium text-slate-800">{b.titel}</div>
                </div>
                <button {...anker('sigeplan.block-bearbeiten', blockIndex === 0)} onClick={() => setEditBlock(b)} className="text-slate-400 hover:text-blue-500" title="Inhalt bearbeiten">
                  <Settings2 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(b.id)} className="text-slate-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Verfügbare Vorlagen</div>
          {/* Ohne Plan-Datensatz fehlt der Fremdschlüssel — der Klick liefe sonst
              ohne jede Rückmeldung ins Leere. */}
          {!sigeProjektId && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mb-2">
              Erst verfügbar, wenn der SiGe-Plan angelegt ist — bitte den Assistenten durchlaufen.
            </div>
          )}
          <div className="space-y-2">
            {AVAILABLE_BLOCKS.map((ab, vorlagenIndex) => (
              <button
                key={ab.typ}
                {...anker('sigeplan.block-hinzufuegen', vorlagenIndex === 0)}
                onClick={() => handleAddBlock(ab.typ, ab.label)}
                disabled={!sigeProjektId}
                className="w-full text-left bg-white border border-slate-200 p-2.5 rounded-lg text-sm font-medium text-slate-700 hover:border-blue-300 hover:bg-blue-50 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:bg-white"
              >
                {ab.label}
                <Plus className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </div>

      </div>

      {editBlock && editBlock.typ === 'notrufplan' && sigeProjektId && (
        <NotrufplanEditor
          sigeProjektId={sigeProjektId}
          block={editBlock}
          beteiligte={beteiligte}
          adresse={adresse}
          onClose={() => setEditBlock(null)}
          onSaved={handleSaved}
        />
      )}

      {editBlock && editBlock.typ !== 'notrufplan' && (
        <BlockEditor
          block={editBlock}
          onClose={() => setEditBlock(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};
